import React, { useEffect, useRef, useState } from 'react';
import { SupportPromptGroupProps } from '@amzn/awsui-chat-components-console/support-prompt-group';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Container from '@amzn/awsui-components-console/container';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import Header from '@amzn/awsui-components-console/header';
import Link from '@amzn/awsui-components-console/link';
import SpaceBetween from '@amzn/awsui-components-console/space-between';

import { FittedContainer, ScrollableContainer } from './common-components';
import {
  getInitialMessages,
  getLoadingMessage,
  Message,
  supportPromptItems,
  supportPromptMessageOne,
  supportPromptMessageTwo,
  getUserAuthorId,
} from './config';
import Messages from './messages';
import { UserInfo, api } from '../../services/api';
import { isInIframe, isConnectApp } from '../../utils/iframe';
import { checkContactAttributes, getPersonId, getContactId } from '../../utils/connectSdk';
import { WebSocketManager } from '../../components/WebSocketManager';
import { TempContactInput } from '../../components/TempContactInput';
import { useRecommendations} from '../../hooks/useRecommendations';

import '../../styles/chat.scss';

interface ChatProps {
  user: UserInfo;
  sessionId?: string;
}

export default function Chat({ user, sessionId: initialSessionId }: ChatProps) {
  const waitTimeBeforeLoading = 300;
  // The loading state will be shown for 4 seconds for loading prompt and 1.5 seconds for rest of the prompts
  const waitTimeBeforeResponse = (isLoadingPrompt: boolean = false) => (isLoadingPrompt ? 4000 : 1500);

  const [isGenAiResponseLoading, setIsGenAiResponseLoading] = useState(false);
  const [showConnectAlert, setShowConnectAlert] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId || null);
  const [showTempContactInput, setShowTempContactInput] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Use recommendations hook
  const {
    wsContactId,
    wsStatus,
    recommendations,
    isTypingRecommendation,
    recommendationMode,
    handleRecommendation,
    handleModeChangeComplete,
    initializeWebSocket,
    setWsStatus,
    setIsTypingRecommendation,
  } = useRecommendations(initialSessionId);

  const [messages, setMessages] = useState<Message[]>([]);
  const [summaryEvents, setSummaryEvents] = useState<any[]>([]);

  const onSupportPromptClick = (detail: SupportPromptGroupProps.ItemClickDetail) => {
    let newMessage: Message;

    if (detail.id === 'typescript') {
      newMessage = supportPromptMessageOne;
    }

    if (detail.id === 'expand') {
      newMessage = supportPromptMessageTwo;
    }

    const supportPromptText = supportPromptItems.find(item => item.id === detail.id)?.text;

    const newUserMessage: Message = {
      type: 'chat-bubble',
      authorId: getUserAuthorId(user),
      content: supportPromptText,
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages(prevMessages => [...prevMessages, newUserMessage]);

    setTimeout(() => {
      setIsGenAiResponseLoading(true);
      setMessages(prevMessages => [...prevMessages, getLoadingMessage()]);

      setTimeout(() => {
        setMessages(prevMessages => {
          prevMessages.splice(prevMessages.length - 1, 1, newMessage);
          return prevMessages;
        });

        setIsGenAiResponseLoading(false);
      }, waitTimeBeforeResponse());
    }, waitTimeBeforeLoading);
  };

  const setShowFeedbackDialog = (index: number, show: boolean, isPositive?: boolean) => {
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages];
      const updatedMessage = { 
        ...prevMessages[index], 
        showFeedbackDialog: show,
        isPositiveFeedback: isPositive 
      };
      updatedMessages.splice(index, 1, updatedMessage);
      return updatedMessages;
    });
  };

  const lastMessageContent = messages.length > 0 ? messages[messages.length - 1]?.content : null;

  const handleConnectData = (contactId: string) => {
    setSessionId(contactId);
    setShowConnectAlert(false);
    // Auto-start WebSocket connection
    initializeWebSocket(contactId);
  };

  const handleActionClick = (action: string) => {
    // Append the action as a user message. The synchronous chat response path
    // (/converse) has been removed; wiring recommendation actions to a response
    // is tracked separately and out of scope for this change.
    const actionMessage: Message = {
      type: 'chat-bubble',
      authorId: getUserAuthorId(user),
      content: action,
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages(prev => [...prev, actionMessage]);
  };

  const handleTempConnect = (contactId: string) => {
    setSessionId(contactId);
    initializeWebSocket(contactId);
    setShowTempContactInput(false);
  };

  const handleSummaryEvent = (summary: any) => {
    setSummaryEvents([summary]);
  };

  useEffect(() => {
    setMessages(getInitialMessages(onSupportPromptClick));
    
    // Check Connect status once on load
    if (isInIframe() && isConnectApp()) {
      console.log('In Connect iframe, checking for contact data...');
      
      // Try to get cached values first
      let personId = getPersonId();
      let contactId = getContactId();
      
      console.log('Cached values:', { personId, contactId });
      
      if (personId && contactId) {
        console.log('Found cached contact data, setting up session');
        handleConnectData(contactId);
      } else {
        console.log('No cached data, attempting to fetch contact attributes');
        // Try to fetch contact attributes immediately
        checkContactAttributes().then(success => {
          if (success) {
            const newPersonId = getPersonId();
            const newContactId = getContactId();
            console.log('Fetched contact data:', { newPersonId, newContactId });
            if (newPersonId && newContactId) {
              handleConnectData(newContactId);
            } else {
              setShowConnectAlert(true);
            }
          } else {
            console.log('Failed to fetch contact attributes, showing alert');
            setShowConnectAlert(true);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkConnectData = async () => {
    const success = await checkContactAttributes();
    if (success) {
      const personId = getPersonId();
      const contactId = getContactId();
      
      if (personId && contactId) {
        handleConnectData(contactId);
      }
    }
  };

  useEffect(() => {
    // Scroll to the bottom to show the new/latest message
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 0);
  }, [lastMessageContent]);

  // Add new recommendations to chat timeline
  useEffect(() => {
    if (recommendations.length > 0) {
      const latestRecommendation = recommendations[0]; // Most recent recommendation
      
      const recommendationMessage: Message = {
        type: 'recommendation' as const,
        authorId: 'system-recommendations',
        content: `New Recommendation`,
        timestamp: new Date().toLocaleTimeString(),
        recommendation: latestRecommendation,
        index: recommendations.length - 1
      };

      setMessages(prev => [...prev, recommendationMessage]);
    }
  }, [recommendations.length]); // Only trigger when count changes

  // Add new summary events to chat timeline
  useEffect(() => {
    console.log('Summary events useEffect triggered, length:', summaryEvents.length);
    if (summaryEvents.length > 0) {
      const latestSummary = summaryEvents[0];
      const summaryMessage: Message = {
        type: 'summaryEvent' as const,
        authorId: 'system-summary',
        content: `Previous Conversation Summary`,
        timestamp: new Date().toLocaleTimeString(),
        summaryEvent: latestSummary,
        index: summaryEvents.length - 1
      };
      setMessages([summaryMessage]);
    }
  }, [summaryEvents]);

  // Show loading message when generating recommendations using existing pattern
  useEffect(() => {
    if (isTypingRecommendation) {
      const loadingMessage: Message = {
        type: 'chat-bubble',
        authorId: 'gen-ai',
        content: <Box color="text-status-inactive">Generating recommendations...</Box>,
        timestamp: new Date().toLocaleTimeString(),
        avatarLoading: true,
      };

      setMessages(prev => {
        // Remove any existing loading messages and add new one
        const nonLoadingMessages = prev.filter(msg =>
          !(msg.type === 'chat-bubble' && msg.avatarLoading)
        );
        return [...nonLoadingMessages, loadingMessage];
      });
    } else {
      // Remove loading message when done
      setMessages(prev => prev.filter(msg =>
        !(msg.type === 'chat-bubble' && msg.avatarLoading)
      ));
    }
  }, [isTypingRecommendation]);

  const addMessage = (index: number, message: Message) => {
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages];
      updatedMessages.splice(index, 0, message);
      return updatedMessages;
    });
  };

  return (
    <div className={`chat-container`}>
      {wsContactId && (
        <WebSocketManager
          contactId={wsContactId}
          onRecommendation={handleRecommendation}
          onSummaryEvent={handleSummaryEvent}
          onConnectionStatus={setWsStatus}
          onTypingStatus={setIsTypingRecommendation}
          recommendationMode={recommendationMode}
          onModeChangeComplete={handleModeChangeComplete}
        />
      )}

      {showConnectAlert && (
        <Alert
          type="warning" i18nStrings={{ warningIconAriaLabel: "Warning" }}
          dismissible
          onDismiss={() => setShowConnectAlert(false)}
          action={
            <Button onClick={checkConnectData} variant="primary">
              Retry
            </Button>
          }
        >
          Unable to get personId, Clinical AI Assistant is still usable but won't be able to get specific patient details. When a contact is started please click retry.
        </Alert>
      )}

      {showTempContactInput && (
        <TempContactInput
          onConnect={handleTempConnect}
          onClose={() => setShowTempContactInput(false)}
          isConnected={wsStatus === 'connected'}
        />
      )}

      <FittedContainer>
        <Container
          header={
            <Header 
              variant="h3"
              description={`Welcome, ${user.firstName} ${user.lastName} (${user.username})`}
              actions={
                <SpaceBetween direction="horizontal" size="s">
                  {wsStatus !== 'connected' && !isInIframe() && (
                    <Button
                      variant="normal"
                      iconName="settings"
                      onClick={() => setShowTempContactInput(!showTempContactInput)}
                    >
                      WebSocket Setup
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              Clinical AI Assistant
            </Header>
          }
          fitHeight
          disableContentPaddings
          footer={
            <Box color="text-body-secondary" margin={{ top: 'xs' }} fontSize="body-s">
              Clinical AI Assistant uses generative AI. You may need to verify responses for accuracy.{' '}
              <Link href="https://aws.amazon.com/ai/responsible-ai/policy/" external variant="primary" fontSize="inherit">
                AWS Responsible AI Policy
              </Link>
              .
            </Box>
          }
        >
          <ScrollableContainer ref={messagesContainerRef}>
            <div style={{ padding: '16px' }}>
              <Messages
                messages={messages}
                setShowFeedbackDialog={setShowFeedbackDialog}
                addMessage={addMessage}
                sessionId={sessionId}
                onFeedbackSubmit={async (data: any) => {
                  return api.saveFeedback(data);
                }}
                onActionClick={handleActionClick}
                user={user}
                onNextActionClick={handleActionClick}
              />
            </div>
          </ScrollableContainer>
        </Container>
      </FittedContainer>
    </div>
  );
}
